/**
 * Badge Assignment Modal Component
 * Modal for granting badges to users with search and selection functionality
 * Allows bulk badge assignment with optional reasons for audit trails
 */

import { useState, useMemo } from 'react';
import { useAdminBadgesList, useGrantBadge } from '../../hooks/useBadgeApi';
import { type UserProfile } from '../../lib/types/user';
import { type BadgeDefinition } from '../../lib/types/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { FormButton } from '../form/FormButton';
import { AnimatedMessage } from '../ui/AnimatedMessage';
import { BadgeIcon } from '../badges/BadgeIcon';

/** A badge the user holds by an automatic `entitlement:*` grant (#2061). */
export interface AutomaticGrant {
  entitlement: string;
  hidden: boolean;
}

interface BadgeAssignmentModalProps {
  user: UserProfile;
  currentBadges: string[]; // Array of badge IDs the user already has
  // The subset of `currentBadges` held by an automatic grant, by badge id.
  // These stay selectable, unless automatic-only: granting one converts it to
  // a manual grant, which outlives the entitlement (the grant route answers
  // `converted: true`).
  automaticGrants?: Record<string, AutomaticGrant>;
  isOpen: boolean;
  onClose: () => void;
}

interface AvailableBadgeCardProps {
  badge: BadgeDefinition;
  isSelected: boolean;
  onToggle: (badgeId: string) => void;
  automatic?: AutomaticGrant;
}

function AvailableBadgeCard({
  badge,
  isSelected,
  onToggle,
  automatic,
}: AvailableBadgeCardProps) {
  return (
    <div
      data-testid={`assign-badge-${badge.badgeId}`}
      className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${
        isSelected
          ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20'
          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
      }`}
      onClick={() => onToggle(badge.badgeId)}
    >
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          {/* BadgeIcon rather than a bare <img> with its own onError
              fallback. Badges can now legitimately carry `icon_url: ''`, and
              one component should decide what that looks like instead of five
              renderers each carrying their own copy — this one's copy fell back
              to a third-party dicebear request. */}
          <BadgeIcon
            src={badge.icon_url}
            alt={badge.name}
            size="sm"
            className="rounded-full"
            imageless={badge.imageless}
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {badge.name}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {badge.description}
          </p>
          {automatic && (
            <p
              data-testid={`convert-to-manual-${badge.badgeId}`}
              className="mt-1 text-xs text-amber-700 dark:text-amber-300"
            >
              Convert to manual: held automatically by{' '}
              <code>{automatic.entitlement}</code>
              {automatic.hidden && ' (hidden, and stays hidden)'}. Granting it
              makes it a manual grant that outlives the entitlement.
            </p>
          )}
        </div>
        <div className="flex-shrink-0">
          {isSelected ? (
            <svg
              className="w-5 h-5 text-sky-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded-full" />
          )}
        </div>
      </div>
    </div>
  );
}

export function BadgeAssignmentModal({
  user,
  currentBadges,
  automaticGrants = {},
  isOpen,
  onClose,
}: BadgeAssignmentModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBadges, setSelectedBadges] = useState<string[]>([]);
  const [reason, setReason] = useState('');

  const {
    data: badgesData,
    isLoading: isLoadingBadges,
    error: badgesError,
  } = useAdminBadgesList({ limit: 100 });

  const grantBadge = useGrantBadge();

  // Available: badges the user does not hold, plus those held only by an
  // automatic grant, which an explicit grant converts to manual (#2061). A
  // badge already granted manually is left out; granting it again is a 409.
  // An automatic-only badge (#2268) is never offered: the API refuses any
  // explicit grant of it with 409, so it is not offered for conversion either.
  const availableBadges = useMemo(() => {
    if (!badgesData?.badges) return [];

    return badgesData.badges.filter(
      badge =>
        badge.automatic_only !== true &&
        (!currentBadges.includes(badge.badgeId) ||
          automaticGrants[badge.badgeId] !== undefined)
    );
  }, [badgesData?.badges, currentBadges, automaticGrants]);

  // Filter badges based on search query
  const filteredBadges = useMemo(() => {
    if (!searchQuery) return availableBadges;

    const query = searchQuery.toLowerCase();
    return availableBadges.filter(
      badge =>
        badge.name.toLowerCase().includes(query) ||
        badge.badgeId.toLowerCase().includes(query) ||
        badge.description.toLowerCase().includes(query)
    );
  }, [availableBadges, searchQuery]);

  const handleToggleBadge = (badgeId: string) => {
    setSelectedBadges(prev =>
      prev.includes(badgeId)
        ? prev.filter(id => id !== badgeId)
        : [...prev, badgeId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBadges.length === filteredBadges.length) {
      setSelectedBadges([]);
    } else {
      setSelectedBadges(filteredBadges.map(badge => badge.badgeId));
    }
  };

  const handleSubmit = async () => {
    if (selectedBadges.length === 0) return;

    try {
      // Grant badges one by one (could be optimized with bulk API)
      for (const badgeId of selectedBadges) {
        await grantBadge.mutateAsync({
          userId: user.uid,
          badgeId,
          reason: reason || undefined,
        });
      }

      // Reset form and close modal
      setSelectedBadges([]);
      setReason('');
      setSearchQuery('');
      onClose();
    } catch (_error) {
      // Error is handled by the mutation
    }
  };

  const handleClose = () => {
    if (grantBadge.isPending) return;

    setSelectedBadges([]);
    setReason('');
    setSearchQuery('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={grantBadge.isPending ? undefined : handleClose}
        />

        {/* Modal */}
        <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Grant Badges to {user.display_name}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Select badges to grant to this user
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={grantBadge.isPending}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Search and Selection Controls */}
          <div className="space-y-4 mb-6">
            {/* Search Input */}
            <div>
              <input
                type="text"
                placeholder="Search badges by name, ID, or description..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                disabled={grantBadge.isPending}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
              />
            </div>

            {/* Selection Controls */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {selectedBadges.length} of {filteredBadges.length} badges
                selected
              </p>
              {filteredBadges.length > 0 && (
                <button
                  onClick={handleSelectAll}
                  disabled={grantBadge.isPending}
                  className="text-sm font-medium text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300 disabled:opacity-50"
                >
                  {selectedBadges.length === filteredBadges.length
                    ? 'Deselect All'
                    : 'Select All'}
                </button>
              )}
            </div>
          </div>

          {/* Available Badges List */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
              Available Badges
            </h4>

            {isLoadingBadges ? (
              <div className="flex items-center justify-center h-32">
                <LoadingSpinner size="lg" />
              </div>
            ) : badgesError ? (
              <AnimatedMessage type="error" message="Failed to load badges" />
            ) : filteredBadges.length === 0 ? (
              <div className="text-center py-8">
                <svg
                  className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {searchQuery
                    ? 'No badges match your search'
                    : 'No available badges'}
                </p>
                {!searchQuery && availableBadges.length === 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    This user has earned all available badges
                  </p>
                )}
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto space-y-2">
                {filteredBadges.map(badge => (
                  <AvailableBadgeCard
                    key={badge.badgeId}
                    badge={badge}
                    isSelected={selectedBadges.includes(badge.badgeId)}
                    onToggle={handleToggleBadge}
                    automatic={automaticGrants[badge.badgeId]}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Reason Field */}
          {selectedBadges.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reason (optional)
              </label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={3}
                placeholder="Explain why these badges are being granted..."
                disabled={grantBadge.isPending}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This will be logged for audit purposes
              </p>
            </div>
          )}

          {/* Error Message */}
          {grantBadge.error && (
            <div className="mb-6">
              <AnimatedMessage
                type="error"
                message={grantBadge.error?.message ?? 'Failed to grant badges'}
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <FormButton
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={grantBadge.isPending}
              className="flex-1"
            >
              Cancel
            </FormButton>
            <FormButton
              type="button"
              variant="primary"
              onClick={() => {
                handleSubmit().catch(() => {
                  // Error handled by mutation
                });
              }}
              disabled={selectedBadges.length === 0 || grantBadge.isPending}
              isLoading={grantBadge.isPending}
              loadingText={`Granting ${selectedBadges.length} badge${selectedBadges.length !== 1 ? 's' : ''}...`}
              className="flex-1"
            >
              Grant {selectedBadges.length} Badge
              {selectedBadges.length !== 1 ? 's' : ''}
            </FormButton>
          </div>
        </div>
      </div>
    </div>
  );
}
